import { useEffect, useState, useCallback } from "react";
import {
  LayoutDashboard, Gauge, CalendarClock, Percent, BarChart3, Newspaper,
  Landmark, Settings2, Sparkles, Loader2, GripVertical, ArrowUp, ArrowDown, Youtube,
} from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { CategoryView } from "@/components/CategoryView";
import { MarkdownLite } from "@/components/MarkdownLite";
import { ConnectGate } from "@/components/ConnectGate";
import { useLang } from "@/context/LanguageContext";
import { RiskGauge } from "@/components/RiskGauge";
import { EconomicCalendar } from "@/components/EconomicCalendar";
import { InterestRates } from "@/components/InterestRates";
import { LiveNewsFeed } from "@/components/LiveNewsFeed";
import { MacroData } from "@/components/MacroData";
import { CotPositioning } from "@/components/CotPositioning";
import { LiveStream } from "@/components/LiveStream";
import { WatchlistBar } from "@/components/WatchlistBar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";

const CATALOG = {
  live: { titleKey: "w.live", icon: Youtube, kind: "vidéo en direct" },
  sentiment: { titleKey: "w.sentiment", icon: Gauge, kind: "sentiment de risque" },
  calendar: { titleKey: "w.calendar", icon: CalendarClock, kind: "événement économique" },
  rates: { titleKey: "w.rates", icon: Percent, kind: "taux d'intérêt" },
  macro: { titleKey: "w.macro", icon: BarChart3, kind: "donnée macro" },
  news: { titleKey: "w.news", icon: Newspaper, kind: "actualité" },
  cot: { titleKey: "w.cot", icon: Landmark, kind: "positionnement COT" },
};
const ALL_IDS = Object.keys(CATALOG);
const DEFAULT = ["live", "sentiment", "calendar", "rates", "macro", "news"];

function WidgetBody({ id, kind }) {
  if (id === "live") return <LiveStream />;
  if (id === "sentiment") return <RiskGauge compact />;
  if (id === "calendar") return <EconomicCalendar compact />;
  if (id === "rates") return <InterestRates compact />;
  if (id === "news") return <LiveNewsFeed compact />;
  if (id === "macro") return <MacroData compact />;
  if (id === "cot") return <CotPositioning compact />;
  return <CategoryView cat={id} kind={kind} compact />;
}

function Widget({ id, t }) {
  const c = CATALOG[id];
  if (!c) return null;
  const Icon = c.icon;
  const wide = id === "calendar" || id === "news" || id === "live";
  return (
    <div className={`flex h-full flex-col rounded-lg border border-[#222733] bg-[#11141A] p-4 transition-colors hover:border-[#2E3648] sm:p-5 ${wide ? "md:col-span-2" : ""}`} data-testid={`widget-${id}`}>
      <div className="mb-3 flex items-center gap-2 border-b border-[#222733] pb-2.5">
        <Icon className="h-4 w-4 text-blue-400" strokeWidth={1.8} />
        <h3 className="font-heading text-sm font-semibold text-slate-100">{t(c.titleKey)}</h3>
      </div>
      <div className="flex-1">
        <WidgetBody id={id} kind={c.kind} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { t, lang } = useLang();
  const [layout, setLayout] = useState(DEFAULT);
  const [ready, setReady] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);

  useEffect(() => {
    api.get("/dashboard/config").then(({ data }) => {
      let widgets = Array.isArray(data.widgets) && data.widgets.length
        ? data.widgets.filter((id) => ALL_IDS.includes(id))
        : [...DEFAULT];
      if (!localStorage.getItem("liveWidgetAdded")) {
        if (!widgets.includes("live")) {
          widgets = ["live", ...widgets];
          api.put("/dashboard/config", { widgets }).catch(() => {});
        }
        localStorage.setItem("liveWidgetAdded", "1");
      }
      setLayout(widgets);
      setReady(true);
    }).catch(() => setReady(true));
  }, []);

  const persist = useCallback((next) => {
    setLayout(next);
    api.put("/dashboard/config", { widgets: next }).catch(() => {});
  }, []);

  const toggle = (id) => {
    persist(layout.includes(id) ? layout.filter((x) => x !== id) : [...layout, id]);
  };
  const move = (id, dir) => {
    const i = layout.indexOf(id);
    const j = i + dir;
    if (j < 0 || j >= layout.length) return;
    const next = [...layout];
    [next[i], next[j]] = [next[j], next[i]];
    persist(next);
  };

  const runSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.post("/ai/summarize", { scope: "aujourd'hui", lang });
      setSummary(data.summary);
    } catch (e) {
      toast.error(t("dash.summaryFail"));
    } finally {
      setSummaryLoading(false);
    }
  };

  return (
    <div>
      <PageHeader
        title={t("dash.title")}
        subtitle={t("dash.sub")}
        icon={LayoutDashboard}
        actions={
          <Dialog open={customOpen} onOpenChange={setCustomOpen}>
            <DialogTrigger asChild>
              <Button variant="secondary" className="gap-2 border-slate-700 bg-slate-800 hover:bg-slate-700" data-testid="customize-btn">
                <Settings2 className="h-4 w-4" /> {t("dash.customize")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md border-slate-800 bg-[#12151C]" data-testid="customize-dialog">
              <DialogHeader><DialogTitle className="font-heading">{t("dash.customizeTitle")}</DialogTitle></DialogHeader>
              <div className="space-y-1.5">
                {ALL_IDS.map((id) => {
                  const active = layout.includes(id);
                  const Icon = CATALOG[id].icon;
                  return (
                    <div key={id} className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <GripVertical className="h-4 w-4 text-slate-600" />
                        <Icon className="h-4 w-4 text-slate-400" />
                        <span className="text-sm text-slate-200">{t(CATALOG[id].titleKey)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        {active && (
                          <>
                            <button onClick={() => move(id, -1)} className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-white" data-testid={`up-${id}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                            <button onClick={() => move(id, 1)} className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-white" data-testid={`down-${id}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                          </>
                        )}
                        <Switch checked={active} onCheckedChange={() => toggle(id)} data-testid={`toggle-${id}`} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      <ConnectGate>
      <div className="mb-6">
        <WatchlistBar />
      </div>
      <div className="mb-6 rounded-lg border border-blue-900/40 bg-blue-950/20 p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-blue-400" />
            <h3 className="font-heading text-sm font-semibold text-slate-100">{t("dash.summaryTitle")}</h3>
          </div>
          <Button size="sm" onClick={runSummary} disabled={summaryLoading} className="gap-1.5 bg-blue-600 hover:bg-blue-500" data-testid="summary-btn">
            {summaryLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            {t("dash.generate")}
          </Button>
        </div>
        {summary ? (
          <div className="mt-3 text-sm text-slate-200"><MarkdownLite text={summary} /></div>
        ) : (
          <p className="mt-2 text-xs text-slate-500">{t("dash.summaryHint")}</p>
        )}
      </div>

      {ready && (
        <div className="grid grid-cols-1 gap-4 sm:gap-5 md:grid-cols-2 xl:grid-cols-3">
          {layout.map((id) => <Widget key={id} id={id} t={t} />)}
        </div>
      )}
      {ready && layout.length === 0 && (
        <p className="text-sm text-slate-500">{t("dash.noWidgets")}</p>
      )}
      </ConnectGate>
    </div>
  );
}
